"use client";
import { t } from "@/lib/i18n";
import { useState, useEffect } from "react";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";
import { useRouter } from "next/navigation";
import { normalizeApiUrl } from "@/lib/api";
import { getAllowedModules } from "@/lib/module-access";
import { emitRbacAccessChanged } from "@/lib/use-rbac-access";
import Cookies from "js-cookie";

type PermissionScope = "all" | "own";

function normalizeStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function normalizePermissionScopes(value: unknown): Record<string, PermissionScope> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce<Record<string, PermissionScope>>(
    (scopes, [permission, scope]) => {
      if (scope === "all" || scope === "own") {
        scopes[permission] = scope;
      }

      return scopes;
    },
    {},
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const router = useRouter();

  // Clear legacy bloated cookies on mount to avoid WAF 403 Forbidden errors
  useEffect(() => {
    Cookies.remove("rbac_permissions");
    Cookies.remove("rbac_permission_scopes");
    Cookies.remove("user_allowed_modules");
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    let didSucceed = false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 60000);

    try {
      const loginUrl = normalizeApiUrl(
        `${process.env.NEXT_PUBLIC_API_URL || "/api"}/login`,
      );
      const res = await fetch(loginUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        signal: controller.signal,
      });
      const data = await res.json();

      if (!res.ok) {
        setError(
          t(data.message) || t("Login failed. Please check your credentials."),
        );
        return;
      }

      const user = data.user || {};
      const displayRole =
        typeof user.role === "string" && user.role.length > 0
          ? user.role
          : "view_only";
      const rbacRoles = normalizeStringArray(user.rbac_roles);
      const rbacPermissions = normalizeStringArray(user.rbac_permissions);
      const rbacPermissionScopes = normalizePermissionScopes(
        user.rbac_permission_scopes,
      );
      const allowedModules = getAllowedModules(user.allowed_modules);
      const normalizedUser = {
        ...user,
        role: displayRole,
        rbac_roles: rbacRoles,
        rbac_permissions: rbacPermissions,
        rbac_permission_scopes: rbacPermissionScopes,
        allowed_modules: allowedModules,
      };

      // Save cookies
      const isSecure = window.location.protocol === "https:";
      const cookieOptions = {
        expires: 1,
        secure: isSecure,
        sameSite: "strict" as const,
      };
      Cookies.set("alita_token", data.token, cookieOptions);
      Cookies.set("user_role", displayRole, cookieOptions);
      Cookies.set("user_id", String(user.id), cookieOptions);
      Cookies.set("user_email", String(user.email || ""), cookieOptions);
      Cookies.set("rbac_roles", JSON.stringify(rbacRoles), cookieOptions);

      // Save to localStorage
      localStorage.setItem("token", data.token);
      localStorage.setItem("lastActivity", Date.now().toString());
      localStorage.removeItem("logoutAt");
      localStorage.setItem("user_id", String(user.id));
      localStorage.setItem("user_name", String(user.name || "Account User"));
      localStorage.setItem("user_role", displayRole);
      localStorage.setItem("user_email", String(user.email || ""));
      if (user.job_title) {
        localStorage.setItem("user_job_title", String(user.job_title));
      } else {
        localStorage.removeItem("user_job_title");
      }
      localStorage.setItem("rbac_roles", JSON.stringify(rbacRoles));
      localStorage.setItem("rbac_permissions", JSON.stringify(rbacPermissions));
      localStorage.setItem(
        "rbac_permission_scopes",
        JSON.stringify(rbacPermissionScopes),
      );
      localStorage.setItem("user_data", JSON.stringify(normalizedUser));
      localStorage.setItem(
        "user_allowed_modules",
        JSON.stringify(allowedModules),
      );
      emitRbacAccessChanged();

      didSucceed = true;
      router.replace("/tasks");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        setError(t("Login timeout. Please try again."));
      } else {
        setError(t("Network error. Please try again."));
      }
    } finally {
      window.clearTimeout(timeoutId);
      if (!didSucceed) {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen w-full relative flex items-center justify-center p-4 overflow-hidden">
      {/* Simple Modern Gradient Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-zinc-800 to-black">
        {/* Soft orange gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/20 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-bl from-transparent via-transparent to-orange-600/15" />

        {/* White glow areas */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-white/10 to-transparent blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-white/8 to-transparent blur-3xl" />

        {/* Orange accent glows */}
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-orange-500/20 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-1/3 right-1/4 w-[350px] h-[350px] bg-orange-600/15 rounded-full blur-3xl animate-pulse-glow animation-delay-3000" />

        {/* Subtle grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)`,
            backgroundSize: "50px 50px",
          }}
        />

        {/* Vignette effect */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.4)_100%)]" />
      </div>

      {/* White Login Card */}
      <div className="relative w-full max-w-md z-10">
        {/* Outer glow effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 via-orange-400 to-orange-500 rounded-3xl blur-2xl opacity-20" />

        {/* Solid white card */}
        <div className="relative bg-white rounded-3xl shadow-2xl p-8 md:p-10">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="relative group">
              <div className="absolute inset-0 bg-orange-500 rounded-2xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity" />
              <div className="relative bg-black rounded-2xl p-4">
                <img
                  src="/aibas.png"
                  alt={t("AiBAS Logo")}
                  className="h-10 w-auto"
                  loading="eager"
                />
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-black mb-2">{t("Welcome Back")}</h1>
            <p className="text-gray-500 text-sm">{t("Sign in to your account")}</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-600 text-sm font-medium">{error}</p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                {t("Email")}
              </label>
              <div className="relative group">
                <Mail
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-orange-500 transition-colors"
                  size={20}
                />
                <input
                  id="email"
                  type="email"
                  required
                  disabled={isLoading}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl text-black placeholder-gray-400 focus:border-orange-500 focus:bg-white focus:outline-none transition-all disabled:opacity-50"
                  placeholder={t("name@company.com")}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                {t("Password")}
              </label>
              <div className="relative group">
                <Lock
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-orange-500 transition-colors"
                  size={20}
                />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  disabled={isLoading}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl text-black placeholder-gray-400 focus:border-orange-500 focus:bg-white focus:outline-none transition-all disabled:opacity-50"
                  placeholder={t("Enter your password")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full mt-6 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-4 rounded-xl shadow-lg shadow-orange-500/30 transition-all duration-200 hover:shadow-orange-500/50 hover:scale-[1.02] active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                t("Sign In")
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-center text-xs text-gray-500">
              {t("© 2026 AiBAS Platform. All rights reserved.")}
            </p>
          </div>
        </div>
      </div>

      {/* Animations are now in globals.css to avoid _jsxDEV reference errors */}
    </div>
  );
}
